const ENCRYPTION_VERSION = 2
const AES_ALGORITHM = "AES-GCM"
const AES_KEY_LENGTH = 32
const IV_LENGTH = 12
const RSA_ALGORITHM = { name: "RSA-OAEP", hash: "SHA-1" }
const BASE64_SLICE_LENGTH = 0x8000

let serverPublicKeyPromise = null

const base64ToBytes = base64 => Uint8Array.from(atob(base64), char => char.charCodeAt(0))

const bytesToBase64 = bytes => {
  let binary = ""

  for (let offset = 0; offset < bytes.length; offset += BASE64_SLICE_LENGTH) {
    binary += String.fromCharCode.apply(null, bytes.subarray(offset, offset + BASE64_SLICE_LENGTH))
  }

  return btoa(binary)
}

const pemToBytes = pem =>
  base64ToBytes(
    pem
      .replace(/\\n/g, "")
      .replace(/-----(BEGIN|END) PUBLIC KEY-----/g, "")
      .replace(/\s/g, "")
  )

const getServerPublicKey = () => {
  if (!serverPublicKeyPromise) {
    serverPublicKeyPromise = crypto.subtle
      .importKey(
        "spki",
        pemToBytes(process.env.VUE_APP_RESQUEST_RSA_PUBLIC_KEY),
        RSA_ALGORITHM,
        false,
        ["encrypt"]
      )
      .catch(error => {
        serverPublicKeyPromise = null
        throw error
      })
  }

  return serverPublicKeyPromise
}

const supportsGzip = () => typeof DecompressionStream !== "undefined"

const gunzip = async bytes => {
  const stream = new Blob([bytes]).stream().pipeThrough(new DecompressionStream("gzip"))

  return new Response(stream).text()
}

export const createCipherSession = async () => {
  const rawKey = crypto.getRandomValues(new Uint8Array(AES_KEY_LENGTH))

  const [aesKey, wrappedKey] = await Promise.all([
    crypto.subtle.importKey("raw", rawKey, AES_ALGORITHM, false, ["encrypt", "decrypt"]),
    getServerPublicKey().then(publicKey =>
      crypto.subtle.encrypt({ name: RSA_ALGORITHM.name }, publicKey, rawKey)
    )
  ])

  const headers = {
    "X-Encryption-Version": String(ENCRYPTION_VERSION),
    "X-Encryption-Key": bytesToBase64(new Uint8Array(wrappedKey))
  }

  if (supportsGzip()) {
    headers["X-Encryption-Accept-Gzip"] = "1"
  }

  return { aesKey, headers }
}

export const isEncryptedPayload = payload =>
  !!payload &&
  payload.v === ENCRYPTION_VERSION &&
  typeof payload.iv === "string" &&
  typeof payload.content === "string"

export const encryptWithSession = async (session, object) => {
  const iv = crypto.getRandomValues(new Uint8Array(IV_LENGTH))

  const encrypted = await crypto.subtle.encrypt(
    { name: AES_ALGORITHM, iv },
    session.aesKey,
    new TextEncoder().encode(JSON.stringify(object))
  )

  return {
    iv: bytesToBase64(iv),
    content: bytesToBase64(new Uint8Array(encrypted))
  }
}

export const decryptWithSession = async (session, payload) => {
  const decrypted = new Uint8Array(
    await crypto.subtle.decrypt(
      { name: AES_ALGORITHM, iv: base64ToBytes(payload.iv) },
      session.aesKey,
      base64ToBytes(payload.content)
    )
  )

  const text = payload.compressed ? await gunzip(decrypted) : new TextDecoder().decode(decrypted)

  return JSON.parse(text)
}
